// A production batch (WP5.2): what was planned and with which recipe version, what the
// kitchen used (per component: the recipe's need against what was taken, and the lots it
// came from — traceability), the output lot, the yield against plan and input, the actual
// cost, the flags, and its history. Cancel it while it's planned or started.

import { brand } from '@enp/brand';
import type { BatchDetail } from '@enp/domain/api';
import { canOpenArea } from '@enp/domain/permissions';
import { subQty, qtyToString } from '@enp/domain/quantity';
import { formatPlainDate } from '@enp/domain/time';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  cn,
  EmptyState,
  Money,
  PageHeader,
  Qty,
  Skeleton,
} from '@enp/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  notFound,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { ArrowLeft, Ban, History, PackageSearch, Printer, ScrollText } from 'lucide-react';
import { useState } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { ApiError } from '../../../lib/api.js';
import {
  BatchStatusBadge,
  BeyondStock,
  cell,
  Fact,
  Facts,
  Note,
  numCell,
  Panel,
  PlainTable,
  useCan,
  UsageDifference,
  Variance,
} from '../../../production/parts.js';
import { CancelBatchDialog } from '../../../production/cancel-dialog.js';
import { batchQuery } from '../../../production/queries.js';
import {
  amountText,
  batchFlagDetail,
  cancelSentence,
  CONSUMPTION_SOURCE_LABEL,
  costDifferenceCauses,
  DESTINATION_LABEL,
  differencePct,
  RECIPE_AMOUNT_ASSUMED,
  shortOrderLine,
  unitCostText,
} from '../../../production/words.js';
import { AccountDate } from '../../../shell/account-date.js';
import { MetaList, useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { StockStateBadge } from '../../../stock/components.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/production/$batchId')({
  loader: async ({ context, params }) => {
    if (!UUID_RE.test(params.batchId)) throw notFound();
    try {
      await context.queryClient.ensureQueryData(batchQuery(params.batchId));
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: BatchPage,
  pendingComponent: BatchSkeleton,
  errorComponent: BatchError,
  notFoundComponent: BatchNotFound,
});

const TRANSITION_LABEL: Record<BatchDetail['transitions'][number]['transition'], string> = {
  start: 'Started',
  complete: 'Completed',
  cancel: 'Cancelled',
};

const person = (p: { name: string | null } | null) => p?.name ?? 'Someone';

function YieldPanel({ b }: { b: BatchDetail }) {
  const unit = b.product.base_uom;
  // A cancelled batch isn't waiting for anything (UI5A-10).
  const cancelled = b.status === 'cancelled';
  return (
    <Panel id="yield" title="Yield">
      <Facts>
        <Fact label="Planned">{amountText(b.planned_qty, unit)}</Fact>
        <Fact label="Recipe scaled for">
          {b.input_qty ? (
            amountText(b.input_qty, unit)
          ) : (
            <span className="text-muted">{cancelled ? '—' : 'Not completed yet'}</span>
          )}
        </Fact>
        <Fact label="Came out">
          {b.actual_yield_qty ? (
            <span className="font-display text-xl font-semibold tabular-nums">
              {amountText(b.actual_yield_qty, unit)}
            </span>
          ) : (
            <span className="text-muted">
              {cancelled ? 'Cancelled: nothing made' : 'Not completed yet'}
            </span>
          )}
        </Fact>
        {b.yield_variance && (
          <>
            <Fact label="Against the plan">
              <Variance value={b.yield_variance.vs_planned} unit={unit} />
            </Fact>
            <Fact label="Against what the recipe should give">
              <Variance value={b.yield_variance.vs_recipe} unit={unit} />
            </Fact>
          </>
        )}
      </Facts>
    </Panel>
  );
}

function OutputPanel({ b }: { b: BatchDetail }) {
  const planned = b.allocations.filter((a) => a.kind === 'planned');
  const actual = b.allocations.filter((a) => a.kind === 'actual');
  // A cancelled batch goes nowhere: its plan isn't shown as still coming (UI5A-10).
  const shown = actual.length > 0 ? actual : b.status === 'cancelled' ? [] : planned;
  return (
    <Panel id="output" title="Output">
      {b.output_lot ? (
        <div className="flex flex-col gap-2">
          <p className="text-body text-fg">
            Lot{' '}
            <span className="whitespace-nowrap font-mono font-semibold">
              {b.output_lot.lot_code}
            </span>
            {b.output_lot.best_before ? (
              <>, best before {formatPlainDate(b.output_lot.best_before)}</>
            ) : (
              ', no best-before'
            )}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" asChild>
              <Link to="/stock/movements" search={{ product: b.product.id, lot: b.output_lot.id }}>
                <History aria-hidden />
                Where the lot went
              </Link>
            </Button>
          </div>
          <p className="flex items-start gap-1.5 text-sm text-muted">
            <Printer aria-hidden className="mt-0.5 size-4 shrink-0" />
            Reprint its label at a production station: open the batch there, or scan the lot and
            choose Print label.
          </p>
        </div>
      ) : (
        <p className="text-body text-muted">
          {b.status === 'cancelled'
            ? 'Cancelled before it was made: no lot.'
            : 'The output lot is made when a station completes the batch; its label prints there.'}
        </p>
      )}
      {shown.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <h3 className="text-sm font-semibold text-fg">
            {actual.length > 0 ? 'Where it went' : 'Where it should go'}
          </h3>
          <ul className="flex flex-col gap-1 text-body">
            {shown.map((a) => (
              <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-fg">
                  {DESTINATION_LABEL[a.destination]}
                  {a.location && <span className="text-muted"> · {a.location.name}</span>}
                  {a.to_branch && <span className="text-muted"> · {a.to_branch.name}</span>}
                  {a.order_line && (
                    <span className="text-muted">
                      {' '}
                      · {a.order_line.order_number} line {a.order_line.position}
                    </span>
                  )}
                </span>
                <Qty value={a.qty} unit={b.product.base_uom} className="tabular-nums" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

function CostPanel({ b }: { b: BatchDetail }) {
  const c = b.cost;
  const unit = b.product.base_uom;
  if (b.status !== 'completed') {
    return (
      <Panel id="cost" title="Actual cost">
        <p className="text-body text-muted">
          {b.status === 'cancelled'
            ? 'No cost: it was cancelled before it was made.'
            : 'Worked out when the batch is completed.'}
        </p>
      </Panel>
    );
  }
  const causes = costDifferenceCauses(b);
  const at = b.completed_at;
  return (
    <Panel
      id="cost"
      title="Actual cost"
      description="What was used, at each component’s standard cost (purchase prices per delivery come later)."
    >
      <Facts>
        <Fact label="Actual">
          {c.actual_cents === null ? (
            <span className="text-muted">Not worked out</span>
          ) : (
            <>
              <Money cents={c.actual_cents} className="font-display text-xl font-semibold" />
              {c.actual_unit_cents !== null && (
                <span className="block text-sm text-muted">
                  {unitCostText(c.actual_unit_cents, unit)}
                </span>
              )}
            </>
          )}
        </Fact>
        <Fact label="Standard, for what came out">
          {c.standard_cents === null ? (
            <span className="text-muted">No standard cost</span>
          ) : (
            <>
              <Money cents={c.standard_cents} />
              {c.standard_unit_cents !== null && (
                // A snapshot: the product's standard when the batch was completed (UI5A-7).
                <span className="block text-sm text-muted">
                  Standard at completion
                  {at && (
                    <>
                      {' '}
                      (<AccountDate value={at} />)
                    </>
                  )}
                  : {unitCostText(c.standard_unit_cents, unit)}
                </span>
              )}
            </>
          )}
        </Fact>
      </Facts>
      {c.actual_cents !== null && c.standard_cents !== null && (
        <div className="flex flex-col gap-1.5">
          <p className="text-body text-fg">
            Difference:{' '}
            {c.actual_cents === c.standard_cents ? (
              'none'
            ) : (
              <>
                <Money
                  cents={c.actual_cents - c.standard_cents}
                  signed
                  className={
                    c.actual_cents > c.standard_cents ? 'text-state-danger' : 'text-state-success'
                  }
                />{' '}
                {c.actual_cents > c.standard_cents ? 'more than standard' : 'less than standard'}
              </>
            )}
          </p>
          {causes.length > 0 && (
            // Why, one cause per line (UI5A-7).
            <ul
              aria-label="Why it differs"
              className="flex flex-col gap-1 border-l-2 border-border pl-3 text-sm"
            >
              {causes.map((x) => (
                <li key={x.key} className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="text-fg">{x.label}</span>
                  <Money cents={x.cents} signed className="tabular-nums text-fg" />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {!c.complete && (
        <Note>Incomplete: no cost for {c.missing.map((m) => m.name).join(', ')}.</Note>
      )}
    </Panel>
  );
}

function ConsumptionPanel({ b }: { b: BatchDetail }) {
  if (b.components.length === 0 && b.consumption.length === 0) {
    return (
      <Panel id="consumption" title="What it used">
        <p className="text-body text-muted">
          {b.status === 'cancelled'
            ? 'Nothing: it was cancelled before it was made.'
            : 'Nothing yet. Components are taken from stock when a station completes the batch, oldest lots first unless the baker scans the ones used.'}
        </p>
      </Panel>
    );
  }
  return (
    <Panel
      id="consumption"
      title="What it used"
      description="The recipe’s need, scaled to the batch, against what was recorded as taken, and the lots it came from. Only an amount the baker entered at the station can differ from the recipe; the rest is the recipe’s amount, not a weighing."
    >
      {/* Beyond stock rides on Recorded, and each lot's amount sits under Recorded as
          "taken" (UI5A-103, UI5A-104): no figure in a column it doesn't belong to. */}
      <PlainTable
        label="Components used"
        className="-mx-5"
        head={['Component', 'Recipe needs', 'Recorded', 'Difference']}
        align={['left', 'right', 'right', 'right']}
      >
        {b.components.map((c) => {
          const unit = c.component.base_uom;
          const rows = b.consumption.filter((r) => r.component.id === c.component.id);
          const diff = qtyToString(subQty(c.consumed, c.theoretical));
          const entered = c.qty_basis === 'entered';
          const edge = rows.length > 0 ? 'border-b-0' : '';
          return [
            <tr key={c.component.id}>
              <th scope="row" className={`${cell} ${edge} pl-5 text-left font-medium text-fg`}>
                {c.component.name}
              </th>
              <td className={`${numCell} ${edge}`}>
                <Qty value={c.theoretical} unit={unit} maxDecimals={6} />
              </td>
              <td className={`${numCell} ${edge}`}>
                <span className="inline-flex flex-col items-end gap-0.5 leading-tight">
                  <Qty value={c.consumed} unit={unit} maxDecimals={6} />
                  <span className="text-xs font-normal text-muted">
                    {entered ? 'Entered at the station' : RECIPE_AMOUNT_ASSUMED}
                  </span>
                  <BeyondStock qty={c.beyond_stock} unit={unit} />
                </span>
              </td>
              <td className={`${numCell} ${edge} pr-5`}>
                {entered ? (
                  <UsageDifference
                    diff={diff}
                    pct={differencePct(diff, c.theoretical)}
                    unit={unit}
                  />
                ) : (
                  // Equal by construction: there is no difference to show (UI5A-4).
                  <span className="text-muted">
                    <span aria-hidden>—</span>
                    <span className="sr-only">Not weighed</span>
                  </span>
                )}
              </td>
            </tr>,
            ...rows.map((r, i) => {
              const last = i === rows.length - 1 ? 'pt-1 pb-3' : 'border-b-0 py-1';
              return (
                <tr key={r.movement_id} data-testid="lot-row" className="text-sm">
                  <td colSpan={2} className={cn(cell, last, 'pl-5')}>
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 border-l-2 border-border pl-3">
                      <span className="sr-only">Lot </span>
                      <span className="whitespace-nowrap font-mono text-fg">
                        {r.lot?.lot_code ?? 'No lot'}
                      </span>
                      <StockStateBadge state={r.state} />
                      <span className="text-muted">
                        {r.location.name} · {CONSUMPTION_SOURCE_LABEL[r.source]}
                      </span>
                    </span>
                  </td>
                  <td className={cn(numCell, last)}>
                    <Qty value={r.qty} unit={unit} maxDecimals={6} className="text-fg" />{' '}
                    <span className="text-muted">taken</span>
                  </td>
                  <td className={cn(cell, last, 'pr-5')} />
                </tr>
              );
            }),
          ];
        })}
      </PlainTable>
    </Panel>
  );
}

function HistoryPanel({ b }: { b: BatchDetail }) {
  const entries: { key: string; label: string; at: string; by: string | null }[] = [
    {
      key: 'planned',
      // Then "Started" follows: a station batch was created there (UI5A-109).
      label:
        b.origin === 'admin' ? `Planned in the ${brand.appNames.admin}` : 'Created at a station',
      at: b.created_at,
      by: b.created_by ? person(b.created_by) : null,
    },
    ...b.transitions.map((t) => ({
      key: `${t.transition}-${t.occurred_at}`,
      label: TRANSITION_LABEL[t.transition],
      at: t.occurred_at,
      by: t.by ? person(t.by) : null,
    })),
  ];
  return (
    <Panel id="history" title="History">
      <ol className="flex flex-col gap-2 text-body">
        {entries.map((e) => (
          // What happened, then who and when below it: the same for every entry (UI5A-20).
          <li key={e.key} className="flex flex-col">
            <span className="font-medium text-fg">{e.label}</span>
            <span className="text-sm text-muted">
              {e.by && <>by {e.by} · </>}
              <AccountDate value={e.at} withTime />
            </span>
          </li>
        ))}
      </ol>
    </Panel>
  );
}

function BatchPage() {
  const { batchId } = Route.useParams();
  const { data: b } = useSuspenseQuery(batchQuery(batchId));
  useDocumentTitle(`Batch ${b.number}`);
  const canPlan = useCan('production.plan');
  const { data: me } = useQuery(meQueryOptions());
  const seesOrders = me ? canOpenArea(me.permissions, 'orders') : false;
  const seesPlanning = me ? canOpenArea(me.permissions, 'planning') : false;
  const [cancelling, setCancelling] = useState(false);
  const cancellable = b.status === 'planned' || b.status === 'started';

  return (
    <>
      <PageHeader
        eyebrow={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/production" search={{ day: b.planned_day }}>
                    Production
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{b.number}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
        title={b.product.name}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <BatchStatusBadge status={b.status} />
            <MetaList
              items={[
                <span key="n" className="font-mono text-sm">
                  {b.number}
                </span>,
                <AccountDate key="d" value={b.planned_start} withTime />,
                b.branch.name,
                b.station?.name,
              ]}
            />
          </span>
        }
        actions={
          <>
            <Button variant="secondary" asChild>
              <Link to="/recipes/$recipeId" params={{ recipeId: b.recipe_version.recipe_id }}>
                <ScrollText aria-hidden />
                Recipe version {b.recipe_version.version_no}
              </Link>
            </Button>
            {cancellable && canPlan && (
              <Button
                variant="destructive"
                icon={<Ban aria-hidden />}
                onClick={() => setCancelling(true)}
              >
                Cancel batch…
              </Button>
            )}
          </>
        }
      />

      <div className="mb-6 flex flex-col gap-3">
        {b.flags.length > 0 && (
          <Note>
            <p className="font-semibold">
              {b.flags.length === 1 ? '1 thing to look at' : `${b.flags.length} things to look at`}
            </p>
            <ul className="mt-1 flex list-disc flex-col gap-1 pl-5">
              {b.flags.map((f, i) => {
                const pid = f.data?.['product_id'];
                const line = shortOrderLine(f, b);
                return (
                  <li key={`${f.code}-${i}`}>
                    {batchFlagDetail(f, b)}
                    {line && seesOrders && (
                      <>
                        {' '}
                        <Link
                          to="/orders/$orderId"
                          params={{ orderId: line.order_id }}
                          className="font-medium text-primary-text underline underline-offset-4"
                        >
                          Open {line.order_number}
                        </Link>
                      </>
                    )}
                    {line && seesPlanning && (
                      <>
                        {' · '}
                        <Link
                          to="/planning"
                          search={{ focus: line.id, branch: b.branch.id }}
                          className="font-medium text-primary-text underline underline-offset-4"
                        >
                          See it on the planning board
                        </Link>
                      </>
                    )}
                    {f.code === 'component_shortage' && typeof pid === 'string' && (
                      <>
                        {' '}
                        <Link
                          to="/stock/$productId"
                          params={{ productId: pid }}
                          className="font-medium text-primary-text underline underline-offset-4"
                        >
                          See its stock
                        </Link>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </Note>
        )}
        {b.cancelled_at && (
          <Note tone={b.status === 'completed' ? 'warning' : 'neutral'}>
            {cancelSentence(b)} (<AccountDate value={b.cancelled_at} withTime />)
            {b.status === 'completed' && ' It was completed at a station afterwards.'}
          </Note>
        )}
        {b.note && (
          <p className="text-body text-fg">
            <span className="font-medium">Note: </span>
            {b.note}
          </p>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-6">
          <YieldPanel b={b} />
          <ConsumptionPanel b={b} />
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          <OutputPanel b={b} />
          <CostPanel b={b} />
          <HistoryPanel b={b} />
        </div>
      </div>

      {cancelling && <CancelBatchDialog batch={b} onClose={() => setCancelling(false)} />}
    </>
  );
}

function BatchSkeleton() {
  return (
    <div role="status" aria-live="polite">
      <h1 className="sr-only">Loading batch</h1>
      <span className="sr-only">Loading batch…</span>
      <div className="flex flex-col gap-2 pb-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-5 w-96 max-w-full" />
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex flex-col gap-6">
          <Skeleton className="h-40 rounded-lg" />
          <Skeleton className="h-72 rounded-lg" />
        </div>
        <div className="flex flex-col gap-6">
          <Skeleton className="h-40 rounded-lg" />
          <Skeleton className="h-40 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function BackButton() {
  return (
    <Button variant="secondary" asChild>
      <Link to="/production">
        <ArrowLeft aria-hidden />
        Back to production
      </Link>
    </Button>
  );
}

function BatchError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <div className="max-w-2xl">
      <ErrorPanel
        error={error}
        what="This batch"
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
        extraAction={<BackButton />}
      />
    </div>
  );
}

function BatchNotFound() {
  useDocumentTitle('Batch not found');
  return (
    <div className="max-w-2xl">
      <EmptyState
        icon={PackageSearch}
        titleAs="h1"
        title="Batch not found"
        description="There’s no batch at this address. It may be at a branch you can’t see, or the link may be mistyped."
        action={<BackButton />}
      />
    </div>
  );
}
